<template>
  <v-container py-0>
    <v-progress-linear
      :active="loading"
      height="3"
      :indeterminate="true"
      class="my-1"
    />
    <v-card color="transparent" flat light>
      <v-card-text class="py-0">
        <v-alert :value="emailAlreadyRegisted" type="error">
          {{ $t('error.error.emailAlreadyRegisted') }}
        </v-alert>
        <v-form autocomplete="off">
          <v-layout row wrap>
            <v-flex md3 sm6 xs12 px-2>
              <v-text-field
                v-model="localUser.name"
                v-validate="'required'"
                :label="$t('fields.global.name') + ' (*)'"
                :data-vv-name="$t('fields.global.name')"
                :error-messages="errors.collect($t('fields.global.name'))"
                autocomplete="new-text"
                browser-autocomplete
              />
            </v-flex>
            <v-flex md3 sm6 xs12 px-2>
              <v-text-field
                v-model="localUser.lastname1"
                v-validate="'required'"
                :label="$t('fields.users.lastname') + ' (*)'"
                :data-vv-name="$t('fields.users.lastname')"
                :error-messages="errors.collect($t('fields.users.lastname'))"
                autocomplete="new-text"
                browser-autocomplete
              />
            </v-flex>
            <v-flex md3 sm6 xs12 px-2>
              <v-text-field
                v-model="localUser.lastname2"
                :label="$t('fields.users.lastname2')"
                autocomplete="new-text"
                browser-autocomplete
              />
            </v-flex>
            <v-flex md3 sm6 xs12 px-2>
              <v-text-field
                v-model="localUser.password"
                v-validate="!localUser._id ? 'min:6' : ''"
                :label="$t('fields.users.password')"
                counter
                :type="showPassword ? 'text' : 'password'"
                :append-icon="showPassword ? 'visibility' : 'visibility_off'"
                :data-vv-name="$t('fields.users.password')"
                :error-messages="errors.collect($t('fields.users.password'))"
                autocomplete="new-password"
                browser-autocomplete
                @click:append="showPassword = !showPassword"
              />
            </v-flex>
            <v-flex md3 sm6 xs12 px-2>
              <v-text-field
                v-model="localUser.dni"
                :label="$t('fields.users.dni')"
                autocomplete="new-text"
                browser-autocomplete
              />
            </v-flex>
            <v-flex md3 sm6 xs12 px-2>
              <v-select
                v-model="localUser.gender"
                v-validate="'required'"
                :items="genders"
                :label="$t('fields.users.sex') + ' (*)'"
                :data-vv-name="$t('fields.users.sex')"
                :error-messages="errors.collect($t('fields.users.sex'))"
                @focus="(e) => e.target.click()"
              />
            </v-flex>
            <v-flex md3 sm6 xs12 px-2>
              <v-menu
                ref="menu"
                v-model="menu"
                :close-on-content-click="false"
                :nudge-right="40"
                full-width
                lazy
                min-width="290px"
                offset-y
                transition="scale-transition"
              >
                <v-text-field
                  slot="activator"
                  v-model="birthdayDate"
                  v-validate="'required'"
                  :label="$t('fields.users.birthdayDate') + ' (*)'"
                  prepend-icon="event"
                  readonly
                  :data-vv-name="$tc('fields.users.birthdayDate')"
                  :error-messages="
                    errors.collect($tc('fields.users.birthdayDate'))
                  "
                />
                <v-date-picker
                  ref="picker"
                  v-model="localUser.birthdayDate"
                  min="1930-01-01"
                  :max="new Date().toISOString().split('T')[0]"
                  first-day-of-week="1"
                  :locale="$store.state.languages.iso()"
                />
              </v-menu>
            </v-flex>
            <v-flex md3 sm6 xs12 px-2>
              <v-text-field
                v-model="localUser.email"
                v-validate="'required|email'"
                :label="$t('fields.users.email') + ' (*)'"
                :data-vv-name="$t('fields.users.email')"
                :error-messages="errors.collect($t('fields.users.email'))"
                autocomplete="new-text"
                browser-autocomplete
                :loading="loadingEmail"
                @change="onChangeEmail"
              />
            </v-flex>
            <v-flex lg3 md6 xs12 px-2>
              <CountryField
                v-model="localUser.countryId"
                clearable
                :label="
                  $t('action.actions.select', { field: $tc('models.country') })
                "
                autocomplete="new-text"
                browser-autocomplete
              />
            </v-flex>
            <v-flex lg3 md6 xs12 px-2>
              <RegionField
                v-model="localUser.regionId"
                :disabled="!localUser.countryId"
                :country-id="localUser.countryId"
                clearable
                :label="
                  $t('action.actions.select', { field: $tc('models.region') })
                "
                autocomplete="new-text"
                browser-autocomplete
              />
            </v-flex>
            <v-flex lg3 md6 xs12 px-2>
              <ProvinceField
                v-model="localUser.provinceId"
                :disabled="!localUser.regionId"
                :region-id="localUser.regionId"
                clearable
                :label="
                  $t('action.actions.select', { field: $tc('models.province') })
                "
                autocomplete="new-text"
                browser-autocomplete
              />
            </v-flex>
            <v-flex lg3 md6 xs12 px-2>
              <TownField
                v-model="localUser.townId"
                :disabled="!localUser.provinceId"
                :province-id="localUser.provinceId"
                :label="
                  $t('action.actions.select', { field: $tc('models.town') })
                "
                autocomplete="new-text"
                browser-autocomplete
              />
            </v-flex>
            <v-flex md4 sm6 xs12 px-2>
              <v-text-field
                v-model="localUser.address"
                :label="$t('fields.users.address')"
                autocomplete="new-text"
                browser-autocomplete
              />
            </v-flex>
            <v-flex md2 sm6 xs12 px-2>
              <v-text-field
                v-model="localUser.cp"
                :label="$t('fields.users.cp')"
                autocomplete="new-text"
                browser-autocomplete
              />
            </v-flex>
            <v-flex md2 sm6 xs12 px-2>
              <v-text-field
                v-model="localUser.telf"
                :label="$t('fields.users.phone')"
                autocomplete="new-text"
                browser-autocomplete
              />
            </v-flex>
            <template v-if="!profileView">
              <template v-if="isPlayer">
                <v-flex md2 sm6 xs12 px-2>
                  <v-text-field
                    v-model="localUser.fedCode"
                    :label="$t('titles.entities.fedCode')"
                  />
                </v-flex>
                <v-flex
                  v-if="is(['president', 'coordinator'])"
                  md3
                  sm6
                  xs12
                  px-2
                >
                  <v-switch
                    v-model="localUser.isClubPaymentDone"
                    :label="$t('fields.users.isClubPaymentDone')"
                    color="primary"
                  />
                </v-flex>
              </template>
              <v-flex md2 sm6 xs12 px-2>
                <v-switch
                  v-model="localUser.active"
                  :label="$t('fields.global.active')"
                  color="primary"
                />
              </v-flex>
              <v-flex md2 sm6 xs12 px-2>
                <v-switch
                  v-model="localUser.validated"
                  :label="$t('fields.users.validated')"
                  color="primary"
                />
              </v-flex>
              <v-flex md2 sm6 xs12 px-2>
                <v-switch
                  v-model="localUser.deleted"
                  :label="$t('fields.users.deleted')"
                  color="primary"
                />
              </v-flex>
            </template>
            <v-flex xs12>
              <v-layout>
                <v-flex md4 sm6 xs12 px-2>
                  <v-text-field
                    v-model="titleFile"
                    :label="$t('fields.users.userImage')"
                    disabled
                    prepend-icon="attach_file"
                  />
                  <v-layout row wrap justify-center align-center>
                    <upload-button
                      :file-changed-callback="fileChanged"
                      small
                      :title="titleUpload"
                    />
                  </v-layout>
                  <PhotoPicker @change="fileChanged" />
                </v-flex>
                <v-flex v-if="image" md4 sm6 xs12 px-2>
                  <img :src="image" alt="user-photo" style="max-width: 220px" />
                </v-flex>
              </v-layout>
            </v-flex>
          </v-layout>
        </v-form>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import vuetifyToast from 'vuetify-toast'

import UploadButton from 'vuetify-upload-button'
import { mapGetters, mapState } from 'vuex'
import CountryField from '@/components/widgets/Input/CountryField'
import ProvinceField from '@/components/widgets/Input/ProvinceField'
import RegionField from '@/components/widgets/Input/RegionField'
import TownField from '@/components/widgets/Input/TownField'
import PhotoPicker from '@/components/widgets/PhotoPicker.vue'
import { apiAxios } from '@/helpers'
import { formatDate, formatDatePicker } from '@/helpers/dateHelper'
export default {
  name: 'StepInformation',
  components: {
    CountryField,
    RegionField,
    ProvinceField,
    TownField,
    UploadButton,
    PhotoPicker,
  },
  props: {
    user: {
      type: Object,
      required: true,
      default: () => {},
    },
    profileView: {
      type: Boolean,
      required: false,
      default: false,
    },
    isPlayer: {
      type: Boolean,
      default: false,
    },
    file: { type: [File, String], default: '', required: false },
  },
  data() {
    return {
      showPassword: false,
      menu: false,
      image: '',
      titleFile: '',
      loadingEmail: false,
      emailAlreadyRegisted: false,
    }
  },
  watch: {
    'selected.photo': {
      handler: function (val) {
        if (val) {
          if (val.includes('http')) this.image = val
          else this.image = this.defaultValues.imageUrl + val
        } else {
          this.image = ''
        }

        this.titleFile = val
      },
      immediate: true,
      deep: true,
    },
    menu(val) {
      if (val) {
        this.$nextTick(() => {
          if (this.$refs.picker) this.$refs.picker.activePicker = 'YEAR'
        })
      }
    },
  },
  computed: {
    ...mapState({
      loading: (state) => state.users.loading,
      selected: (state) => state.users.selected,
    }),
    ...mapGetters({
      genders: 'users/genders',
    }),
    localUser: {
      get() {
        return this.user
      },
      set(value) {
        this.$emit('update:user', value)
      },
    },
    formatDatePicker() {
      return formatDatePicker
    },
    birthdayDate() {
      return formatDate(this.localUser.birthdayDate)
    },
    titleUpload() {
      return this.image
        ? this.$t('action.actions.change', {
            field: this.$t('fields.users.image'),
          })
        : this.$t('action.actions.upload', {
            field: this.$t('fields.users.image'),
          })
    },
  },
  methods: {
    clear() {
      this.$validator.reset()
      this.file = ''
      this.image = ''
      this.file = ''
    },
    fileChanged(file) {
      if (file.size < 1) return
      this.$emit('update:file', file)
      this.titleFile = file.name
      const reader = new FileReader()
      const vm = this

      reader.onload = (e) => {
        vm.image = e.target.result
      }
      reader.readAsDataURL(file)
    },
    onChangeEmail(email) {
      this.loadingEmail = true
      apiAxios
        .post('users/simplified', { email })
        .then(({ data }) => {
          this.emailAlreadyRegisted =
            data.data && data.data.user && data.data.user.length > 0
          if (this.emailAlreadyRegisted) {
            vuetifyToast.show({
              text: this.$t('error.error.emailAlreadyRegisted'),
              color: 'error',
              timeout: 2000,
              icon: 'warning',
            })
          }
        })
        .finally(() => {
          this.loadingEmail = false
        })
    },
  },
}
</script>
